import { Reveal } from "./motion-primitives";
import { useLang, Rich } from "@/i18n/LanguageContext";

const SectionLabel = ({ n, children, onDark = false }) => (
  <div className={`mb-14 flex items-center gap-5 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] md:mb-20 ${onDark ? "text-mist" : "text-[#53615f]"}`}>
    <span className={onDark ? "text-sage" : "text-[#98a3a1]"}>{n}</span>
    {children}
  </div>
);

export const About = () => {
  const { c } = useLang();
  const a = c.about;
  return (
    <section id="about" data-testid="section-about" className="bg-paper px-6 py-24 text-ink md:px-[8vw] md:py-32">
      <Reveal>
        <SectionLabel n="01">{a.label}</SectionLabel>
      </Reveal>
      <div className="grid gap-12 md:grid-cols-[1.05fr_0.95fr] md:gap-[11vw]">
        <Reveal>
          <h2 className="font-serif text-[clamp(44px,5.2vw,80px)] font-normal leading-[0.98] tracking-[-0.02em]">
            <Rich text={a.title} />
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="max-w-[590px] pt-2">
          <p className="mb-7 font-sans text-[22px] leading-[1.55]">{a.lead}</p>
          <p className="mb-6 font-sans text-[15px] leading-[1.8] text-[#596360]">{a.p2}</p>
          <p className="font-sans text-[15px] leading-[1.8] text-[#596360]">{a.p3}</p>
          <a href="#capabilities" data-testid="about-link" className="mt-6 inline-block border-b border-ink pb-1 font-sans text-xs text-ink transition-colors duration-300 hover:border-teal hover:text-teal">
            {a.link} <span>↗</span>
          </a>
        </Reveal>
      </div>

      <div className="mt-20 grid border-t border-ink/15 md:mt-28 md:grid-cols-3">
        {a.principles.map((p, i) => (
          <Reveal key={p.t} delay={i * 0.08}>
            <div className={`min-h-[150px] border-b border-ink/15 py-6 pr-[6vw] md:border-b-0 md:border-r md:py-7 ${i === 2 ? "md:border-r-0" : ""} ${i > 0 ? "md:pl-[3vw]" : ""}`}>
              <strong className="mb-4 block font-serif text-[27px] font-normal">{p.t}</strong>
              <span className="font-sans text-xs leading-[1.65] text-[#63706d]">{p.d}</span>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-20 overflow-hidden md:mt-28">
        <div className="group relative aspect-[16/7] w-full overflow-hidden">
          <img
            src="/images/osrelis-2.jpg"
            alt="Aerial view of offshore jacket foundations under installation"
            className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
            style={{ filter: "saturate(0.9) contrast(1.03)" }}
            data-testid="about-image"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(7,19,23,.35), transparent 60%)" }} />
          <span className="absolute bottom-5 left-5 font-sans text-[10px] uppercase tracking-[0.16em] text-white/85">{a.caption}</span>
        </div>
      </Reveal>

      <Reveal className="mt-20 grid items-center gap-10 md:mt-24 md:grid-cols-[300px_1fr] md:gap-16">
        <div className="group overflow-hidden">
          <img
            src="/images/osrelis-24.jpg"
            alt="OSRELIS leadership"
            className="aspect-[3/4] w-full max-w-[300px] object-cover object-top grayscale transition-[filter] duration-[900ms] group-hover:grayscale-0"
            data-testid="leadership-image"
          />
        </div>
        <div>
          <p className="mb-6 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a3a1]">{a.leadership.label}</p>
          <h3 className="font-serif text-[clamp(30px,3.4vw,52px)] font-normal leading-[1.05] tracking-[-0.02em]">{a.leadership.name}</h3>
          <p className="mt-3 font-sans text-[11px] uppercase tracking-[0.16em] text-teal">{a.leadership.role}</p>
          <p className="mt-7 max-w-[620px] font-sans text-[15px] leading-[1.8] text-[#596360]">{a.leadership.statement}</p>
        </div>
      </Reveal>
    </section>
  );
};

export { SectionLabel };
